import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetAllAdvertisersFullQuery } from "../../api/advertiserAPI";
import { useGetAllBrandsFullQuery } from "../../api/brandsAPI";
import {
  useChangeCampaignNumberMutation,
  useGetAllCampaignsQuery,
  useLazyGetCampaignsQuery,
} from "../../api/campaignAPI";
import { useGetAllClientsQuery } from "../../api/clientAPI";
import { useGetAllIndustriesQuery } from "../../api/industryAPI";
import {
  useCreateAndSendProjectMutation,
  useCreateProjectMutation,
  useGetProjectByIdQuery,
  useSetCancelProjectEditingMutation,
  useSetStartProjectEditingMutation,
  useUpdateProjectMutation,
} from "../../api/projectsApi";
import AsyncImage from "../../components/AsyncImage/AsyncImage";
import Button, { ButtonModeE } from "../../components/Button/Button";
import Form from "../../components/Form/Form";
import { ValidatorsE } from "../../components/Form/yupValidations";
import Icon from "../../components/Icon/Icon";
import InfoTooltip from "../../components/InfoTooltip/InfoTooltip";
import { H2, H4, P, Span } from "../../components/Text/Text";
import Tooltip from "../../components/Tooltip/Tooltip";
import {
  AccessLevelE,
  CampaignWizardMode,
  ClientSubtypeE,
  ClientTypeE,
  IconColorE,
  IconSizeE,
  LangE,
  StatusE,
  TooltipPlaceE,
  UserRoleE,
} from "../../models/enums";
import {
  AdvertiserT,
  BrandT,
  ClientT,
  FormFieldT,
  SelectOptionT,
} from "../../models/models";
import { handleCreateCampaign } from "../../projectActions/handleCreateCampaign";
import { handleUpdateCampaign } from "../../projectActions/handleUpdateCampaign";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  selectCurrentLang,
  selectIsFormDirty,
} from "../../store/selectors/appSelectors";
import {
  selectIsQuickSave,
  selectWizardStep,
} from "../../store/selectors/popupSelectors";
import { setIsFormDirty } from "../../store/slices/appSlice";
import { setQuickSave, setWizardStep } from "../../store/slices/popupSlice";
import { TOAST_DURATION } from "../../utils/helpers/constants";
import { selectValue } from "../../utils/helpers/selectOptions";
import { clss } from "../../utils/helpers/styleHelpers";
import { getLoggedUser } from "../../utils/helpers/tokenStorage";
import usePopup from "../../utils/hooks/usePopup";
import { useVersions } from "../../utils/hooks/useVersions";
import { CreateNewAdvertiserShortWithName } from "../CreateNewAdvertiserShort";
import { CreateNewBrandShortWithName } from "../CreateNewBrandShort";
import { CreateNewMediaCompanyShortWithName } from "../CreateNewMediaCompanyShort";
import CreateNewProductionCompanyShort from "../CreateNewProductionCompanyShort";
import UnsavedChangesWarning from "../UnsavedChangesWarning";
import CreateProjectHeader from "./components/CreateProjectHeader/CreateProjectHeader";
import StepsNavigation from "./components/StepsNavigation/StepsNavigation";
import VersionRow from "./components/VersionRow/VersionRow";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import dayjs from "dayjs";
import { getCustomCampaignNameFromFullName } from "../../utils/helpers/getCustomCampaignNameFromFullName";
import useCampaignName from "../../utils/hooks/useCampaignName";
import * as yup from "yup";
import CampaignNameConstructor from "./components/CampaignNameConstructor/CampaignNameConstructor";
import { useTranslate } from "../../utils/hooks/useTranslate";
import {
  DEFAULT_POST_PRODUCTION_COMPANY_ID,
  DEFAULT_PRODUCTION_COMPANY_ID,
  EMPTY_VERSION,
  MEDIA_COMPANY_NONE_ID,
} from "../../utils/constants";
import getImageLink from "../../utils/helpers/getImageLink";
import {
  selectSelectedAdvertiser,
  selectSelectedBrand,
  selectSelectedIndustry,
  selectSelectedMediaCompany,
  selectSelectedProductionCompany,
} from "../../store/selectors/deliveryWizardSelector";
import {
  setSelectedAdvertiser,
  setSelectedBrand,
  setSelectedIndustry,
  setSelectedMediaCompany,
  setSelectedProductionCompany,
} from "../../store/slices/deliveryWizardSlice";

import styles from "./campaignWizard.module.scss";
import getDefaultAdAgencyId from "./helpers/getDefaultAdAgencyId";
import useLastBrandAndIndustryIds from "../Wizard/helpers/useLastBrandAndIndustryIds";
import { useSelectOptions } from "../Wizard/helpers/useSelectOptions";
import { canCreateMediaCompany } from "../Wizard/helpers/canCreateMediaCompany";

type CampaignWizardPropTypes = {
  projectId?: number;
  mode: CampaignWizardMode;
};

const steps = [
  { name: "Campaign Details" },
  {
    name: "Versions",
    tooltipText:
      "Set the required versions for the campaign. Later on, the process, the post-production company will upload the media files according to those versions.",
  },
  { name: "Select participants" },
  {
    name: "Destinations",
    tooltipText:
      'The broadcasters to which the delivery is intended should be marked with a "V"',
  },
];

const CampaignWizard: FC<CampaignWizardPropTypes> = (props) => {
  const {
    user: { user },
  } = getLoggedUser();
  const { projectId, mode } = props;
  const { createPopup, removePopup, removeAllPopups } = usePopup();
  const currentStep = useAppSelector(selectWizardStep);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const [searchParams] = useSearchParams();
  const [refetchAllProjects] = useLazyGetCampaignsQuery();
  const isFormDirty = useAppSelector(selectIsFormDirty);
  const isQuickSave = useAppSelector(selectIsQuickSave);
  const lang = useAppSelector(selectCurrentLang);

  const [createProject] = useCreateProjectMutation();
  const [createAndSendProject] = useCreateAndSendProjectMutation();
  const [updateProject] = useUpdateProjectMutation();

  useLastBrandAndIndustryIds();

  // const [advertiserSearchText, setAdvertiserSearchText] = useState('');
  // const [brandSearchText, setBrandSearchText] = useState('');
  // const throttledAdvertisersSearchText = useThrottle(advertiserSearchText);
  // const throttledBrandsSearchText = useThrottle(brandSearchText);

  const { data: project, isFetching: isFetchingProject } =
    useGetProjectByIdQuery({ id: projectId }, { skip: !projectId });
  const { data: campaignSO = [] } = useGetAllCampaignsQuery(
    { projectId, asSelectOptions: true },
    { skip: !projectId }
  );
  const { data: industrySO = [], isLoading: isLoadingIndustries } =
    useGetAllIndustriesQuery({ asSelectOptions: false });
  const {
    data: advertisers = [],
    isFetching: isFetchingAdvertisers,
    isLoading: isLoadingAdvertisers,
  } = useGetAllAdvertisersFullQuery({
    asSelectOptions: false,
    // value: (throttledAdvertisersSearchText.length > 1 ? throttledAdvertisersSearchText : '') || project?.Detailed.AdvertiserName,
  });
  const advertiserSO = useMemo(
    () =>
      (advertisers as any)?.map((adv: AdvertiserT) => ({
        value: adv.Id,
        label: adv.Name,
        shortName: adv.ShortName,
      })),
    [advertisers]
  );
  const {
    data: brands = [],
    isFetching: isFetchingBrands,
    isLoading: isLoadingBrands,
  } = useGetAllBrandsFullQuery({
    asSelectOptions: false,
    // value: (throttledBrandsSearchText.length > 1 ? throttledBrandsSearchText : '') || project?.Detailed.BrandName,
  });
  const brandSO = useMemo(
    () =>
      (brands as any)?.map((brand: BrandT) => ({
        value: +brand.Id,
        label: brand.Name,
        shortName: brand.ShortName,
      })),
    [brands]
  );
  const { data: clients } = useGetAllClientsQuery({});
  const [setStartProjectEditing] = useSetStartProjectEditingMutation();
  const [setCancelProjectEditing] = useSetCancelProjectEditingMutation();
  const [changeCampaignNumber, { isLoading: isChangingCampaignNumber }] =
    useChangeCampaignNumberMutation();

  const [postProductionCompanies, setPostProductionCompanies] = useState([]);
  const [productionCompanies, setProductionCompanies] = useState([]);
  const [advertisingAgencies, setAdvertisingAgencies] = useState([]);
  const [tvChannels, setTvChannels] = useState<ClientT[]>([]);
  const [movieTheatres, setMovieTheatres] = useState<ClientT[]>([]);

  const [isCampaignNumberEditMode, setIsCampaignNumberEditMode] =
    useState(false);
  const [prevCampaignNumber, setPrevCampaignNumber] = useState<SelectOptionT>();
  const [campaignNameLength, setCampaignNameLength] = useState(0);

  const [speakerName, setSpeakerName] = useState("");
  const [participants, setParticipants] = useState("");

  const [step0data, setStep0Data] = useState<any>({});
  const [step2data, setStep2Data] = useState<any>({});

  const [advertiserName, setAdvertiserName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [broadcastingDate, setBroadcastingDate] = useState("");
  const {
    fullCampaignName,
    fullCampaignNameWithoutCustom,
    customCampaignName,
    setCampaignName,
    setCustomCampaignName,
    calcCampaignNameForSubmission,
  } = useCampaignName(project?.Name);
  const [isNameEditMode, setIsNameEditMode] = useState(false);

  const selectedAdvertiser = useAppSelector(selectSelectedAdvertiser);
  const selectedBrand = useAppSelector(selectSelectedBrand);
  const selectedProductionCompany = useAppSelector(
    selectSelectedProductionCompany
  );
  const selectedIndustry = useAppSelector(selectSelectedIndustry);
  const selectedMediaCompany = useAppSelector(selectSelectedMediaCompany);

  // Media companies scoped to the campaign creator's own country (mediaCompaniesSO resolves the
  // country from the creating user's client record — see useSelectOptions). Reused unchanged from
  // the new wizard (spec 09), so the self-delivery flow mirrors the agency flow.
  const { mediaCompaniesSO } = useSelectOptions();

  const [newMediaCompanyName, setNewMediaCompanyName] = useState("");

  // The always-present "Self Media Buy" sentinel (creator buys its own media; the payload builders
  // map it to null) prepended to the creator-country-scoped media companies.
  const mediaCompanyOptions = useMemo(
    () => [
      { value: MEDIA_COMPANY_NONE_ID, label: t`Self Media Buy` },
      ...mediaCompaniesSO,
    ],
    [mediaCompaniesSO, t]
  );

  // const [newlyCreatedAdvertiser, setNewlyCreatedAdvertiser] = useState<SelectOptionT>();
  // const [newlyCreatedBrand, setNewlyCreatedBrand] = useState<SelectOptionT>();

  useEffect(() => {
    if (project?.AdvertiserName || project?.BrandName) {
      setAdvertiserName(project?.AdvertiserName);
      setBrandName(project?.BrandName);
    }
  }, [project?.AdvertiserName, project?.BrandName]);

  useEffect(() => {
    if (selectedAdvertiser?.shortName) {
      setAdvertiserName(selectedAdvertiser.shortName);
    }
    if (selectedBrand?.shortName) {
      setBrandName(selectedBrand.shortName);
    }
  }, [selectedAdvertiser?.shortName, selectedBrand?.shortName]);

  // useEffect(() => {
  //     const brand = brandName ? (advertiserName === brandName ? '' : ` | ${brandName}`) : ' | [Brand]';
  //     const date = dayjs(broadcastingDate).isValid() ? dayjs(broadcastingDate).format('YYYY-MM') : '[Broadcasting date]';

  //     setCampaignName(`${date} | ${advertiserName || '[Advertiser]'}${brandName}`);
  // }, [advertiserName, brandName]);

  useEffect(() => {
    if (project?.AdvertiserId && !selectedAdvertiser?.value && advertiserSO) {
      dispatch(
        setSelectedAdvertiser(selectValue(advertiserSO, project?.AdvertiserId))
      );
    }
    if (project?.BrandId && !selectedBrand?.value && brandSO) {
      dispatch(setSelectedBrand(selectValue(brandSO, project?.BrandId)));
    }
    if (
      project?.ProductionCompanyId &&
      !selectedProductionCompany?.value &&
      productionCompanies
    ) {
      dispatch(
        setSelectedProductionCompany(
          selectValue(productionCompanies, project?.ProductionCompanyId)
        )
      );
    }
    if (project?.IndustryId && !selectedIndustry?.value && industrySO) {
      dispatch(
        setSelectedIndustry(selectValue(industrySO, project?.IndustryId))
      );
    }
    // Edit/Copy pre-select only (guarded by `project`); on create the field stays empty ("Select…").
    // A stored null MediaCompanyId (every pre-existing campaign) resolves to the "Self Media Buy"
    // sentinel. Checked against the whole option (not `.value`, which is 0 for the sentinel = falsy).
    if (project && !selectedMediaCompany) {
      dispatch(
        setSelectedMediaCompany(
          selectValue(
            mediaCompanyOptions,
            project?.MediaCompanyId ?? MEDIA_COMPANY_NONE_ID
          )
        )
      );
    }
  }, [
    project?.AdvertiserId,
    project?.BrandId,
    project?.IndustryId,
    project?.MediaCompanyId,
    advertiserSO,
    brandSO,
    industrySO,
    productionCompanies,
    mediaCompanyOptions,
    selectedMediaCompany,
  ]);

  useEffect(() => {
    return () => {
      dispatch(setSelectedAdvertiser(null));
      dispatch(setSelectedBrand(null));
      dispatch(setSelectedProductionCompany(null));
      dispatch(setSelectedIndustry(null));
      dispatch(setSelectedMediaCompany(null));
    };
  }, []);

  useEffect(() => {
    setBroadcastingDate(
      (prev) =>
        step0data.BroadcastingDate ||
        (project
          ? dayjs(project?.BroadcastingDate).toISOString().slice(0, 10)
          : prev)
    );
  }, [step0data, project]);

  // const handleNewlyCreatedAdvertiser = useCallback((e) => {
  //     const newSelectOption = {
  //         value: e.detail.id,
  //         label: e.detail.name,
  //         shortName: e.detail.shortName,
  //     };

  //     setNewlyCreatedAdvertiser(newSelectOption);
  // }, []);

  // const handleNewlyCreatedBrand = useCallback((e) => {
  //     const newSelectOption = {
  //         value: e.detail.id,
  //         label: e.detail.name,
  //         shortName: e.detail.shortName,
  //     };

  //     setNewlyCreatedBrand(newSelectOption);
  // }, []);

  const handleDocumentClick = useCallback((e) => {
    if (
      e.target.name !== "Name" &&
      e.target.tagName !== "IMG" &&
      !e.target.dataset.exception
    ) {
      setIsNameEditMode(false);
    }
  }, []);

  // useEffect(() => {
  //     document.addEventListener('click', handleDocumentClick)
  //     document.addEventListener('setAdvertiserId', handleNewlyCreatedAdvertiser);
  //     document.addEventListener('setBrandId', handleNewlyCreatedBrand);

  //     return () => {
  //         document.removeEventListener('setAdvertiserId', handleNewlyCreatedAdvertiser);
  //         document.removeEventListener('setBrandId', handleNewlyCreatedBrand);
  //     }
  // }, []);

  useEffect(() => {
    const advertiserArg = advertiserName || project?.Detailed?.AdvertiserName;
    const brandArg = brandName || project?.Detailed?.BrandName;
    const nameArg = getCustomCampaignNameFromFullName(project?.Name);

    // TODO: add Copy of if mode is copy
    setCampaignName(advertiserArg, brandArg, nameArg, broadcastingDate);
  }, [advertiserName, brandName, project, broadcastingDate]);

  const createNewAdvertiser = (advertiserName?: string) => {
    const defaultName =
      typeof advertiserName === "object" ? "" : (advertiserName as string);
    createPopup(CreateNewAdvertiserShortWithName(defaultName));
  };

  const createNewBrand = (brandName?: string) => {
    const defaultName =
      typeof brandName === "object" ? "" : (brandName as string);
    createPopup(CreateNewBrandShortWithName(defaultName));
  };

  const handleCreateProdCompany = (prodCompanyName?: string) => {
    createPopup(CreateNewProductionCompanyShort(prodCompanyName));
  };

  const createNewMediaCompany = () => {
    // On success the popup dispatches setSelectedMediaCompany, so the new company auto-selects and
    // the "Clients" tag invalidation refetches the list (spec 09).
    createPopup(CreateNewMediaCompanyShortWithName(newMediaCompanyName));
  };

  const maxAllowedChars = useMemo(() => {
    const max = 60 - fullCampaignNameWithoutCustom.length;
    return max > 0 ? max : 0;
  }, [fullCampaignNameWithoutCustom]);

  const handleToggleEditMode = () => {
    setIsNameEditMode((prev) => {
      if (prev) {
        const triggerValidateEvent = new CustomEvent("triggerFields", {
          detail: {
            fieldNames: ["Name"],
          },
        });
        const form = document.getElementById("step-0");
        form.dispatchEvent(triggerValidateEvent);
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (isNameEditMode) {
      const campaignNameField = document.getElementById("campaign-name");
      campaignNameField?.focus();
    }
  }, [isNameEditMode]);

  // const campaignNumberFormData: FormFieldT[] = [
  //     {
  //         name: 'Id',
  //         selectOptions: isCampaignNumberEditMode ? campaignSO : undefined,
  //         defaultValue: isCampaignNumberEditMode ? selectValue(campaignSO, project?.Campaign?.CampaignId) : project?.Campaign?.Id,
  //         closeMenuOnSelect: false,
  //         onBlur: () => setIsCampaignNumberEditMode(false),
  //         menuFooterContent: !isMobileOrTablet() ? (props) => (
  //             <div className={styles.selectMenuFooter}>
  //                 <Button
  //                     mode={ButtonModeE.LINK}
  //                     onClick={() => handleCloseAndSaveCampaignNumberDropdown(props)}
  //                 >
  //                     Save
  //                 </Button>
  //                 <Button
  //                     mode={ButtonModeE.LINK}
  //                     className={styles.cancel}
  //                     onClick={() => handleCloseAndCancelCampaignNumberDropdown(props)}
  //                     type='button'
  //                 >
  //                     Cancel
  //                 </Button>
  //             </div>
  //         ) : null,
  //     },
  // ];

  const step0Fields: FormFieldT[] = useMemo(
    () =>
      !advertiserSO || !brandSO
        ? []
        : [
            {
              label: "Advertiser",
              name: "AdvertiserId",
              hintText:
                'If the relevant advertiser of the project is not listed, you can add it by clicking "Create new advertiser"',
              controlledValue: selectedAdvertiser,
              // controlledValue: selectValue(advertiserSO, step0data?.AdvertiserId?.value || project?.AdvertiserId),
              // onInput: (value: string) => setAdvertiserSearchText(value),
              onChange: (value: SelectOptionT) => {
                setAdvertiserName((value as any).shortName);
                dispatch(setSelectedAdvertiser(value));
              },
              // onBlur: () => setNewlyCreatedAdvertiser(null),
              selectOptions: advertiserSO,
              extraContent: () =>
                isFetchingAdvertisers && <LoadingSearchTextIcon />,
              createNewOption: createNewAdvertiser,
              validation: !selectedAdvertiser
                ? ValidatorsE.SELECT_REQUIRED
                : undefined,
              addAction: createNewAdvertiser,
            },
            {
              label: "Brand",
              name: "BrandId",
              hintText:
                'If the brand name of the project is not listed, you can add it by clicking "Create a new brand". In addition to the name, add its AD-ID. AD-ID is a unique brand identifier which consists of 4-6 letters in English, allowing identification of the brand in the system. This is also the ID that will appear in the file name throughout the project.',
              controlledValue: selectedBrand,
              onChange: (value: SelectOptionT) => {
                setBrandName((value as any).shortName);
                dispatch(setSelectedBrand(value));
              },
              selectOptions: brandSO,
              extraContent: () => isFetchingBrands && <LoadingSearchTextIcon />,
              createNewOption: () => createNewBrand(brandName),
              validation: !selectedBrand
                ? ValidatorsE.SELECT_REQUIRED
                : undefined,
              addAction: () => createNewBrand(brandName),
            },
            {
              label: "Media Company",
              hintText:
                "Create a Media Company. If there isn't one for this specific campaign, select 'Self Media Buy'.",
              selectOptions: mediaCompanyOptions,
              controlledValue: selectedMediaCompany,
              onInput: (value: string) => setNewMediaCompanyName(value),
              onChange: (option: SelectOptionT) =>
                dispatch(setSelectedMediaCompany(option)),
              validation: selectedMediaCompany
                ? undefined
                : ValidatorsE.SELECT_REQUIRED,
              // Parity guard with the new wizard: a media-company user never reaches this
              // self-delivery wizard (spec 02), so these are no-ops here — kept for consistency so
              // the two wizards cannot drift. See canCreateMediaCompany for the rule.
              createNewOption: canCreateMediaCompany(user.role)
                ? () => createNewMediaCompany()
                : undefined,
              addAction: canCreateMediaCompany(user.role)
                ? () => createNewMediaCompany()
                : undefined,
              props: { disabled: user.role === UserRoleE.MediaCompany },
            },
            {
              label: "Broadcasting date",
              name: "BroadcastingDate",
              hintText: "Please select the intended broadcast date",
              defaultValue:
                step0data.BroadcastingDate ||
                (project
                  ? dayjs(project?.BroadcastingDate).toISOString().slice(0, 10)
                  : broadcastingDate
                  ? dayjs(broadcastingDate).toISOString().slice(0, 10)
                  : undefined),
              type: "date",
              validation: !broadcastingDate
                ? ValidatorsE.REQUIRED_STRING
                : undefined,
              onChange: (value: string) => setBroadcastingDate(value),
              props: {
                min: new Date().toISOString().split("T")[0],
              },
            },
            {
              label: "Market category",
              name: "IndustryId",
              hintText: "Select industry field",
              defaultValue:
                selectValue(industrySO, project?.IndustryId) ||
                step0data.IndustryId,
              validation: selectedIndustry
                ? undefined
                : ValidatorsE.SELECT_REQUIRED,
              controlledValue: selectedIndustry,
              onChange: (value: SelectOptionT) => {
                dispatch(setSelectedIndustry(value));
              },
              selectOptions: industrySO.map(
                ({ DisplayNameEn, DisplayNameHe, Id }) => ({
                  value: Id,
                  label: lang === LangE.EN ? DisplayNameEn : DisplayNameHe,
                })
              ),
            },
            {
              label: "Campaign name",
              name: "Name",
              defaultValue: getCustomCampaignNameFromFullName(project?.Name),
              validation: yup
                .string()
                .matches(/^[^,;]+$/, t`The use of a comma (,) or a semicolon (;) chars is not allowed.`)
                .max(
                  maxAllowedChars,
                  ({ value }) =>
                    `${customCampaignName.length}/${maxAllowedChars} characters maximum`
                ),
              props: {
                disabled: !project && (!advertiserName || !brandName),
                maxLength: maxAllowedChars,
                id: "campaign-name",
              },
              onChange: (value) => {
                setCampaignNameLength(value.length);
                setCustomCampaignName(value);
              },
              extraContent: () => (
                <div
                  className={
                    !project && (!advertiserName || !brandName)
                      ? styles.campaignNameDisabledWrapper
                      : ""
                  }
                >
                  <Icon
                    name={isNameEditMode ? "approved" : "edit"}
                    color={isNameEditMode ? IconColorE.NONE : IconColorE.GRAY}
                    className={styles.saveCampaignNameIcon}
                    onClick={handleToggleEditMode}
                  />
                  <CampaignNameConstructor
                    fullCampaignNameWithoutCustom={
                      fullCampaignNameWithoutCustom
                    }
                    customCampaignName={customCampaignName}
                    setIsNameEditMode={setIsNameEditMode}
                    asText={isNameEditMode}
                  />
                  {isNameEditMode && customCampaignName && (
                    <P className={styles.maxChars}>
                      {customCampaignName.length}/{maxAllowedChars}
                    </P>
                  )}
                </div>
              ),
            },
          ],
    [
      project,
      industrySO,
      lang,
      advertiserSO,
      brandSO,
      isCampaignNumberEditMode,
      isChangingCampaignNumber,
      prevCampaignNumber,
      campaignNameLength,
      campaignSO,
      isFetchingAdvertisers,
      isFetchingBrands,
      currentStep,
      advertiserName,
      brandName,
      broadcastingDate,
      customCampaignName,
      fullCampaignNameWithoutCustom,
      isNameEditMode,
      selectedAdvertiser,
      selectedBrand,
      selectedIndustry,
      selectedMediaCompany,
      mediaCompanyOptions,
    ]
  );

  const handleNoneClick = (elementID) => {
    const element: any = document.getElementById(elementID);
    if (element) {
      element.value = t`NONE`;
    }

    if (elementID === "participantNames") {
      setParticipants(t`NONE`);
    } else {
      setSpeakerName(t`NONE`);
    }
  };

  const step2Fields: FormFieldT[] = useMemo(
    () =>
      !advertisingAgencies.length ||
      !productionCompanies.length ||
      !postProductionCompanies.length
        ? []
        : [
            {
              label:
                user.role === UserRoleE.DeliveryService
                  ? null
                  : "Post/Sound production",
              name: "PostProductionCompanyId",
              selectOptions: postProductionCompanies,
              defaultValue:
                step2data.PostProductionCompanyId ||
                selectValue(
                  postProductionCompanies,
                  project?.PostProductionCompanyId
                ),
              validation: ValidatorsE.SELECT_REQUIRED,
            },
            {
              label: "Presentors/Actors",
              name: "ParticipantNames",
              defaultValue:
                step2data.ParticipantNames ||
                project?.ParticipantNames ||
                participants,
              validation: participants
                ? undefined
                : ValidatorsE.REQUIRED_STRING,
              onChange: (v) => {
                setParticipants(v);
              },
              props: {
                id: "participantNames",
              },
              extraContent: () => (
                <p className={styles.clickNonHint}>
                  <Span>If there are no Presenters/Actors, click</Span>{" "}
                  <span
                    onClick={() => handleNoneClick("participantNames")}
                    className={styles.noneButton}
                  >
                    ‘{<Span>NONE</Span>}’
                  </span>
                </p>
              ),
            },
            {
              label: "Speaker names",
              name: "SpeakerNames",
              defaultValue:
                step2data.SpeakerNames || project?.SpeakerNames || speakerName,
              validation: speakerName ? undefined : ValidatorsE.REQUIRED_STRING,
              onChange: (v) => {
                setSpeakerName(v);
              },
              props: {
                id: "speakerNames",
              },
              // validation: ValidatorsE.REQUIRED_STRING,
              extraContent: () => (
                <p className={styles.clickNonHint}>
                  <Span>If there are no announcer, click</Span>{" "}
                  <span
                    onClick={() => handleNoneClick("speakerNames")}
                    className={styles.noneButton}
                  >
                    ‘{<Span>NONE</Span>}’
                  </span>
                </p>
              ),
            },
            {
              label: "Production location",
              name: "ProductionLocationIsrael",
              tabOptions: [
                {
                  value: "local",
                  label: "Local",
                },
                {
                  value: "abroad",
                  label: "Abroad",
                },
              ],
              defaultValue:
                (step2data.ProductionLocationIsrael?.value === "local"
                  ? 1
                  : 0) || (project?.ProductionLocationIsrael ? 1 : 0),
            },
            {
              label: "Production company",
              name: "ProductionCompanyId",
              hintText: "Create new production company",
              selectOptions: productionCompanies,
              controlledValue: selectedProductionCompany,
              onChange: (option) =>
                dispatch(setSelectedProductionCompany(option)),
              validation: selectedProductionCompany
                ? undefined
                : ValidatorsE.SELECT_REQUIRED,
              // createNewOption: handleCreateProdCompany,
              // addAction: handleCreateProdCompany,
            },
            {
              label: "Advertising agency",
              name: "AdvertisingAgencyId",
              hintText:
                "If you are representing an advertising agency other than yourself in this campaign, you can select the desired agency from the list.If the name of the desired advertising agency does not appear in this list, please contact us for support.",
              selectOptions: advertisingAgencies,
              defaultValue:
                step2data.AdvertisingAgencyId ||
                advertisingAgencies.find(({ label, value }) =>
                  user?.accessLevel === AccessLevelE.DeliveryServicePrivate
                    ? +value === +user?.id
                    : label === project?.AdvertisingAgencyName
                ),
              validation: ValidatorsE.SELECT_AD_AG_REQUIRED,
            },
          ],
    [
      step2data,
      project,
      productionCompanies,
      advertisingAgencies,
      postProductionCompanies,
      currentStep,
      speakerName,
      participants,
      selectedProductionCompany,
      user?.id,
    ]
  );

  const { versions, addEmptyVersion, saveVersion, deleteVersion, setVersions } =
    useVersions(project?.Versions);

  const handleAddVersion = useCallback(() => {
    // save all edited versions and add new empty one
    const submitVersionButtons = [];
    for (let i = 0; i < versions.length; i++) {
      submitVersionButtons.push(
        document.getElementById(`save-version-row-${i}`)
      );
    }
    submitVersionButtons.forEach((button) => {
      if (button) {
        button.click();
      }
    });
    setTimeout(() => {
      setVersions((prev) =>
        prev ? [...prev, EMPTY_VERSION] : [EMPTY_VERSION]
      );
    }, 0);
  }, [versions]);

  const handleAddEmptyVersion = useCallback(() => {
    setVersions((prev) => (prev ? [...prev, EMPTY_VERSION] : [EMPTY_VERSION]));
  }, []);

  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);

  useEffect(() => {
    if (project) {
      if (mode === CampaignWizardMode.COPY) {
        // set next project number within AD-ID if it is CLONE MODE
        const nextProjectNumber = project.NumberForClient + 1;

        const versionsWithNewNumber = project.Versions?.map((version) => {
          const adIdSections = version.AdId.split("-");
          adIdSections[2] = nextProjectNumber as any;

          return { ...version, AdId: adIdSections.join("-") };
        });
        setVersions(versionsWithNewNumber);
      } else {
        setVersions(project.Versions);
      }
      setSelectedChannels(project.BroadcasterIds?.split(",").map((id) => +id));
    }
  }, [project]);

  const overrideHandleClosePopup = (e: MouseEvent) => {
    if (e.target === e.currentTarget && isFormDirty) {
      e.stopImmediatePropagation();
      createPopup(UnsavedChangesWarning);
    }
  };

  useEffect(() => {
    // const popupOverlay = document.getElementById('popup-overlay');
    const popupCloseIcon = document.getElementById("popup-close-icon");

    // popupOverlay.addEventListener('click', overrideHandleClosePopup);
    popupCloseIcon.addEventListener("click", overrideHandleClosePopup);
    return () => {
      // popupOverlay.removeEventListener('click', overrideHandleClosePopup);
      popupCloseIcon.removeEventListener("click", overrideHandleClosePopup);
    };
  }, [isFormDirty]);

  useEffect(() => {
    dispatch(setQuickSave(false));
    dispatch(setIsFormDirty(false));

    if (mode === CampaignWizardMode.CREATE) {
      setVersions((prev) =>
        prev ? [...prev, EMPTY_VERSION] : [EMPTY_VERSION]
      );
    }

    if (
      mode === CampaignWizardMode.EDIT ||
      (mode === CampaignWizardMode.SHORT_EDIT &&
        project?.StatusName === StatusE.Opened)
    ) {
      setStartProjectEditing({ projectId });
    }

    window.addEventListener("addemptyversion", handleAddEmptyVersion);

    return () => {
      dispatch(setWizardStep(0));
      dispatch(setQuickSave(false));
      dispatch(setIsFormDirty(false));
      if (
        mode === CampaignWizardMode.EDIT ||
        (mode === CampaignWizardMode.SHORT_EDIT &&
          (project?.StatusName === StatusE.Opened ||
            project?.StatusName === StatusE.Editing))
      ) {
        setCancelProjectEditing({ projectId });
      }
      window.removeEventListener("addemptyversion", handleAddEmptyVersion);
    };
  }, []);

  useEffect(() => {
    if (clients) {
      const filteredPostClients = clients.filter(
        (client: ClientT) =>
          +client.ClientType === ClientTypeE["Post/sound production"]
      );
      const filteredProdClients = clients.filter(
        (client: ClientT) =>
          +client.ClientType === ClientTypeE["Production company"]
      );
      const filteredAdvAgClients = clients.filter(
        (client: ClientT) =>
          +client.ClientType === ClientTypeE["Advertising Agency"]
      );
      if (filteredPostClients) {
        const mappedCompanies = filteredPostClients.map((client: ClientT) => ({
          value: +client.Id,
          label: client.Name,
        }));
        setPostProductionCompanies(mappedCompanies);
      }
      if (filteredProdClients) {
        const mappedCompanies = filteredProdClients.map((client: ClientT) => ({
          value: +client.Id,
          label: client.Name,
        }));
        setProductionCompanies([
          {
            value: DEFAULT_PRODUCTION_COMPANY_ID,
            label: t`None`,
          },
          ...mappedCompanies,
        ]);
      }
      if (filteredAdvAgClients) {
        const mappedAdvAgencies = filteredAdvAgClients.map(
          (client: ClientT) => ({ value: +client.Id, label: client.Name })
        );
        setAdvertisingAgencies(mappedAdvAgencies);
      }
      const filteredTvChannels = clients.filter(
        (client: ClientT) =>
          +client.ClientType === ClientTypeE.Broadcaster &&
          client.ClientSubType === ClientSubtypeE.None
      );
      const filteredMovieTheatres = clients.filter(
        (client: ClientT) =>
          +client.ClientType === ClientTypeE.Broadcaster &&
          client.ClientSubType === ClientSubtypeE["Broadcaster - Camera"]
      );
      setTvChannels(filteredTvChannels);
      setMovieTheatres(filteredMovieTheatres);
    }
  }, [clients]);

  useEffect(() => {
    const continueButton = document.getElementById("wizard-continue-button-0");
    const stepsWrapper = document.getElementById("steps-wrapper");

    if (continueButton && stepsWrapper) {
      (continueButton as HTMLButtonElement).disabled = isCampaignNumberEditMode;
      stepsWrapper.style.overflow = !isCampaignNumberEditMode
        ? "hidden"
        : "auto";
    }
  }, [isCampaignNumberEditMode]);

  useEffect(() => {
    const saveButton = document.getElementById("step-3-save-button");
    const saveAndSendButton = document.getElementById(
      "step-3-save-and-send-button"
    );

    const isDisabled = !(selectedChannels?.length > 0);

    saveButton && ((saveButton as any).disabled = isDisabled);
    saveAndSendButton && ((saveAndSendButton as any).disabled = isDisabled);
  }, [selectedChannels]);

  const LoadingSearchTextIcon = useCallback(
    () => (
      <div className={styles.loadingSearchResultsIconWrapper}>
        <Icon name="loading" size={IconSizeE.S} color={IconColorE.LIGHT_GRAY} />
      </div>
    ),
    []
  );

  const handleSelectDestination = (id: number) => {
    if (isNaN(id)) {
      return;
    }
    const existingChannelsSet = new Set(selectedChannels);
    if (existingChannelsSet.has(id)) {
      existingChannelsSet.delete(id);
    } else {
      existingChannelsSet.add(id);
    }
    setSelectedChannels([...(existingChannelsSet as any)]);
  };

  // const handleCloseAndCancelCampaignNumberDropdown = (props) => {
  //     props.setValue(prevCampaignNumber || selectValue(campaignSO, project?.Campaign.CampaignId));
  //     setIsCampaignNumberEditMode(false);
  // };

  // const handleCloseAndSaveCampaignNumberDropdown = async (props) => {
  //     const body = {
  //         CampaignId: props.getValue()[0].value.split('-')[2],
  //         Id: projectId,
  //     };
  //     try {
  //         setPrevCampaignNumber(props.getValue()[0]);
  //         setIsCampaignNumberEditMode(false);
  //         await changeCampaignNumber(body);
  //         toast('Successfully changed', {
  //             type: 'success',
  //             autoClose: TOAST_DURATION,
  //         });
  //     } catch (e: any) {
  //         console.log(e);
  //     }
  // };

  const handleSubmitCampaignData = (
    campaignData: any,
    createAndSend: boolean
  ) => {
    if (
      mode === CampaignWizardMode.CREATE ||
      mode === CampaignWizardMode.COPY
    ) {
      handleCreateCampaign(
        {
          ...campaignData,
          Name: calcCampaignNameForSubmission(
            selectedAdvertiser.shortName,
            selectedAdvertiser.shortName,
            broadcastingDate
          ),
          AdvertiserId: selectedAdvertiser,
          BrandId: selectedBrand,
          MediaCompanyId: selectedMediaCompany,
          IndustryId: selectedIndustry,
          BroadcastingDate: broadcastingDate,
          ProductionCompanyId: selectedProductionCompany,
          PostProductionCompanyId: DEFAULT_POST_PRODUCTION_COMPANY_ID,
        },
        "0",
        false,
        createPopup,
        removePopup,
        removeAllPopups,
        createAndSend ? createAndSendProject : createProject,
        dispatch,
        navigate,
        refetchAllProjects,
        searchParams,
        mode
      );
    } else if (
      mode === CampaignWizardMode.EDIT ||
      mode === CampaignWizardMode.SHORT_EDIT
    ) {
      handleUpdateCampaign(
        {
          ...campaignData,
          Id: projectId,
          Name: calcCampaignNameForSubmission(
            selectedAdvertiser.shortName,
            selectedBrand.shortName,
            broadcastingDate
          ),
          AdvertiserId: selectedAdvertiser,
          BrandId: selectedBrand,
          MediaCompanyId: selectedMediaCompany,
          IndustryId: selectedIndustry,
          ProductionCompanyId: selectedProductionCompany,
          PostProductionCompanyId: DEFAULT_POST_PRODUCTION_COMPANY_ID,
        },
        "0",
        false,
        removePopup,
        updateProject,
        dispatch,
        navigate,
        refetchAllProjects,
        searchParams,
        user
      );
    }
  };

  const submitAllStep0 = (data: any) => {
    const submitAllData = () => {
      const step2data = {
        PostProductionCompanyId: { value: project.PostProductionCompanyId },
        ProductionCompanyId: { value: project.ProductionCompanyId },
        ParticipantNames: project.ParticipantNames,
        SpeakerNames: project.SpeakerNames,
        AdvertisingAgencyId: { value: getDefaultAdAgencyId(user) },
        AdvertisingAgencyName: { value: project.AdvertisingAgencyName },
        ProductionLocationIsrael: project.ProductionLocationIsrael
          ? "local"
          : "abroad",
      };
      const campaignData = {
        ...data,
        ...step2data,
        Versions: versions,
        selectedChannels: project.BroadcasterIds.split(","),
        AdvertiserId: selectedAdvertiser,
        BrandId: selectedBrand,
        Id: projectId,
      };
      handleSubmitCampaignData(campaignData, true);
    };

    // find all unsaved versions and try to save them
    if (currentStep === 1) {
      const saveButtons = getSaveVersionButtons();
      // resolve all save buttons
      saveButtons?.forEach((saveButton) => {
        (saveButton as HTMLButtonElement).click();
      });

      setTimeout(() => {
        const remainingSaveButtons = getSaveVersionButtons();

        if (remainingSaveButtons.length === 0) {
          submitAllData();
        }
      }, 0);
    } else {
      submitAllData();
    }
  };

  const submitAllStep1 = () => {
    const step2data = {
      PostProductionCompanyId: { value: project.PostProductionCompanyId },
      ProductionCompanyId: { value: project.ProductionCompanyId },
      ParticipantNames: project.ParticipantNames,
      SpeakerNames: project.SpeakerNames,
      AdvertisingAgencyId: { value: getDefaultAdAgencyId(user) },
      AdvertisingAgencyName: { value: project.AdvertisingAgencyName },
      ProductionLocationIsrael: project.ProductionLocationIsrael
        ? "local"
        : "abroad",
    };
    const campaignData = {
      ...step0data,
      ...step2data,
      AdvertiseId: selectedAdvertiser,
      BrandId: selectedBrand,
      Versions: versions,
      selectedChannels: project.BroadcasterIds.split(","),
      Id: projectId,
    };
    handleSubmitCampaignData(campaignData, true);
  };

  const submitAllStep2 = (data) => {
    const campaignData = {
      ...step0data,
      ...data,
      Versions: versions,
      selectedChannels,
      Id: projectId,
    };
    handleSubmitCampaignData(campaignData, true);
  };

  const submitStep0 = (data: any) => {
    setStep0Data(data);
    dispatch(setWizardStep(1));
  };

  const getSaveVersionButtons = () => {
    const saveButtons = [];
    // get all save buttons for each version and push them to the array
    versions?.forEach((_, index) => {
      const saveButton = document.getElementById(`save-version-row-${index}`);
      if (saveButton) {
        saveButtons.push(saveButton);
      }
    });
    return saveButtons;
  };

  const submitStep1 = () => {
    const saveButtons = getSaveVersionButtons();
    // resolve all save buttons
    saveButtons?.forEach((saveButton) => {
      (saveButton as HTMLButtonElement).click();
    });
    setTimeout(() => {
      const remainingSaveButtons = getSaveVersionButtons();

      if (remainingSaveButtons.length === 0) {
        dispatch(setWizardStep(2));
      }
    }, 0);
  };

  const submitStep2 = (data: any) => {
    setStep2Data(data);
    dispatch(setWizardStep(3));
  };

  const submitStep3 = async (data, createAndSend) => {
    const campaignData = {
      ...step0data,
      ...step2data,
      Versions: versions,
      selectedChannels,
      Id: projectId,
    };
    if (!selectedChannels?.length) {
      toast.error(t`Please select at least one broadcaster`);
      return;
    }
    handleSubmitCampaignData(campaignData, createAndSend);
  };

  const handleChangeCampaignNumber = async (value: any) => {
    const body = {
      CampaignId: value.Id.value.split("-")[2],
      Id: projectId,
    };
    await changeCampaignNumber(body);
    toast("Successfully changed", {
      type: "success",
      autoClose: TOAST_DURATION,
    });
    setIsCampaignNumberEditMode(false);
  };

  return (
    <>
      <div className={styles.headerWrapper}>
        <CreateProjectHeader currentStep={currentStep} mode={mode} />
        {mode !== CampaignWizardMode.SHORT_EDIT && (
          <StepsNavigation currentStep={currentStep} mode={mode} />
        )}
      </div>
      <div className={styles.stepsWrapper} id="steps-wrapper">
        <div className={styles.stepTitleWrapper}>
          <H2>{steps[currentStep].name}</H2>
          {steps[currentStep]?.tooltipText && (
            <InfoTooltip
              text={steps[currentStep].tooltipText}
              place={TooltipPlaceE.RIGHT}
            />
          )}
        </div>
        <div
          className={clss([
            styles.step,
            currentStep !== 0 ? styles.hiddenStep : "",
          ])}
        >
          {((!!projectId && !!project) || !projectId) &&
          !isLoadingAdvertisers &&
          !isLoadingIndustries &&
          !isLoadingBrands ? (
            <>
              {/* {!!project && (
                                <>
                                    {isCampaignNumberEditMode && (
                                        <Form
                                            fields={campaignNumberFormData}
                                            onSubmit={handleChangeCampaignNumber}
                                            formID='campaign-number-form'
                                            className={styles.campaignNumberForm}
                                        />
                                    )}
                                    <div className={styles.campaignNumberData}>
                                        <p>Campaign number: {isFetchingProject ? '---' : project?.Campaign?.Id}</p>
                                        {!isCampaignNumberEditMode ? (
                                            <Icon
                                                name={isFetchingProject ? 'loading' : 'edit'}
                                                size={IconSizeE.S}
                                                color={isFetchingProject ? IconColorE.GRAY : IconColorE.DARK_GRAY}
                                                onClick={() => setIsCampaignNumberEditMode(true)}
                                            />
                                        ) : (
                                            <>
                                                <Button icon='approved' mode={ButtonModeE.LINK} form='campaign-number-form' />
                                                <Icon
                                                    name='close'
                                                    size={IconSizeE.S}
                                                    color={IconColorE.GRAY}
                                                    onClick={() => setIsCampaignNumberEditMode(false)}
                                                />
                                            </>
                                        )}
                                    </div>
                                </>
                            )} */}
              <Form
                fields={step0Fields}
                onSubmit={isQuickSave ? submitAllStep0 : submitStep0}
                formID="step-0"
                handleDirty
              />
            </>
          ) : (
            <div className={styles.loaderWrapper}>
              <Icon
                name="loading"
                size={IconSizeE.S}
                color={IconColorE.LIGHT_GRAY}
              />
            </div>
          )}
        </div>

        <div
          className={clss([
            styles.step,
            mode === CampaignWizardMode.SHORT_EDIT ? styles.disabledStep : "",
            currentStep !== 1 ? styles.hiddenStep : "",
          ])}
          id="step-1-section"
        >
          <Form
            fields={[]}
            onSubmit={isQuickSave ? submitAllStep1 : submitStep1}
            formID="step-1"
          />
          <div>
            <div className={styles.versionHeader}>
              <P>Version name</P>
              <P>Content type</P>
              <P>Length (sec)</P>
              <p />
            </div>
            {versions?.map((version, index) => (
              <VersionRow
                version={version}
                onSubmit={saveVersion}
                onDelete={deleteVersion}
                index={index}
                versionsAmount={versions?.length || 0}
                key={index}
                advId={user.AdId}
                campaignId={project?.Campaign.CampaignId.split("-")[1]}
                nextProjectNumber={
                  mode === CampaignWizardMode.COPY
                    ? +project?.NumberForClient + 1
                    : +project?.NumberForClient
                }
                mode={mode}
                currentStep={currentStep}
              />
            ))}
            <div className={styles.addVersionButtonWrapper}>
              <Button
                type="button"
                onClick={handleAddVersion}
                mode={ButtonModeE.LINK}
                icon="addCircleFilled"
                tabIndex={9}
              >
                Add version
              </Button>
            </div>
          </div>
        </div>

        {currentStep === 2 && (
          <div
            className={clss([
              styles.step,
              mode === CampaignWizardMode.SHORT_EDIT ? styles.disabledStep : "",
            ])}
            id="step-2-section"
          >
            <Form
              fields={step2Fields}
              onSubmit={isQuickSave ? submitAllStep2 : submitStep2}
              formID="step-2"
              handleDirty
            />
          </div>
        )}

        <div
          className={clss([
            styles.step,
            mode === CampaignWizardMode.SHORT_EDIT ? styles.disabledStep : "",
            currentStep !== 3 ? styles.hiddenStep : "",
          ])}
          id="step-3-section"
        >
          <Form
            fields={[]}
            onSubmit={(data) => submitStep3(data, false)}
            formID="step-3-save"
          />
          <Form
            fields={[]}
            onSubmit={(data) => submitStep3(data, true)}
            formID="step-3-save-and-send"
          />
          <div className={styles.destinationsWrapper}>
            <H4>TV channels</H4>
            <ul>
              {tvChannels.map((broadcaster, index) => (
                <Tooltip key={index} text={broadcaster.Name} delayShow={0}>
                  <li
                    className={
                      selectedChannels.includes(broadcaster.Id)
                        ? styles.selectedDestination
                        : ""
                    }
                    onClick={() => handleSelectDestination(broadcaster.Id)}
                  >
                    {isMobileOrTablet() && (
                      <span className={styles.mobileTitle}>
                        {broadcaster.Name}
                      </span>
                    )}
                    <AsyncImage link={getImageLink({ id: broadcaster.Id })} />
                  </li>
                </Tooltip>
              ))}
            </ul>
            <H4>Movie theaters</H4>
            <ul>
              {movieTheatres.map((broadcaster, index) => (
                <Tooltip key={index} text={broadcaster.Name} delayShow={0}>
                  <li
                    className={
                      selectedChannels.includes(broadcaster.Id)
                        ? styles.selectedDestination
                        : ""
                    }
                    onClick={() => handleSelectDestination(broadcaster.Id)}
                  >
                    {isMobileOrTablet() && (
                      <span className={styles.mobileTitle}>
                        {broadcaster.Name}
                      </span>
                    )}
                    <AsyncImage link={getImageLink({ id: broadcaster.Id })} />
                  </li>
                </Tooltip>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default CampaignWizard;
